import React, { useState, useRef } from "react";
import { X, CheckCircle2, Star, Check, ArrowRight } from "lucide-react";
import { surveyWidgetService } from "../utils/surveyWidgetService";

export default function SurveyWidgetModal({
  survey,
  onClose,
  onDismiss,
  userEmail: initialEmail = "",
  userName: initialName = "",
  primaryColor = "#2563eb",
  alignment = "right"
}) {
  const knownEmail =
    initialEmail ||
    localStorage.getItem("dexkor_customer_email") ||
    localStorage.getItem("dexkor_voter_email") ||
    "";

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [respondentEmail, setRespondentEmail] = useState(knownEmail);
  const [respondentName, setRespondentName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const pageLoadTimeRef = useRef(Date.now());

  const { questions = [], thankYouCard = {}, optionsConfig = {}, shareConfig = {} } = survey || {};
  const sharePage = shareConfig.sharePage || {};

  // Format & Layout configurations matching PublicSurveyPage
  const isLargeFormat = optionsConfig.format === "large";
  const isSinglePageForm = !!optionsConfig.singlePageForm;
  const showProgressBar = optionsConfig.progressBar !== false;

  const isLeftAligned = alignment === "left";

  const handleAnswerChange = (qId, val, autoAdvance = false) => {
    setAnswers((prev) => ({ ...prev, [qId]: val }));

    if (autoAdvance && !isSinglePageForm && currentStep < questions.length - 1) {
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
      }, 250);
    }
  };

  const toggleMultiChoice = (qId, opt) => {
    const list = Array.isArray(answers[qId]) ? answers[qId] : [];
    const updated = list.includes(opt)
      ? list.filter((i) => i !== opt)
      : [...list, opt];
    setAnswers((prev) => ({ ...prev, [qId]: updated }));
  };

  const handleSubmitSurvey = async (e) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const answersPayload = questions.map((q) => {
        const val = answers[q.id];
        let ratingVal = undefined;
        let textVal = undefined;
        let selectedOptions = [];

        if (typeof val === "number") {
          ratingVal = val;
        } else if (typeof val === "string") {
          textVal = val;
          if (q.type !== "long_text" && q.type !== "short_text") {
            selectedOptions = [val];
          }
        } else if (Array.isArray(val)) {
          selectedOptions = val;
          textVal = val.join(", ");
        }

        return {
          questionId: q.id,
          questionType: q.type,
          questionText: q.question,
          rating: ratingVal,
          textAnswer: textVal,
          selectedOptions,
          optionSelected: selectedOptions.join(", ") || textVal,
          answer: val,
        };
      });

      const metadata = {
        url: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        screenResolution:
          typeof window !== "undefined"
            ? `${window.screen?.width || 0}x${window.screen?.height || 0}`
            : "",
        language: typeof navigator !== "undefined" ? navigator.language : "",
        sessionDuration: Math.round((Date.now() - pageLoadTimeRef.current) / 1000),
      };

      await surveyWidgetService.submitResponse(survey._id, {
        respondentName: respondentName || "Anonymous",
        respondentEmail: respondentEmail || "",
        answers: answersPayload,
        metadata,
      });

      // Mark survey as completed in localStorage
      try {
        localStorage.setItem(`dexkor_survey_completed_${survey._id}`, "true");
      } catch (e) {}

      setSubmitted(true);

      // Handle redirect if configured
      if (shareConfig.onSubmission?.redirectEnabled && shareConfig.onSubmission?.redirectUrl) {
        setTimeout(() => {
          if (typeof window !== "undefined") {
            window.location.href = shareConfig.onSubmission.redirectUrl;
          }
        }, 1500);
      } else {
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      }
    } catch (err) {
      console.error("Failed to submit survey response:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper Renderer for Question Types matching PublicSurveyPage
  const renderQuestionInput = (q) => {
    if (!q) return null;

    // NPS Scale 0-10
    if (q.type === "nps") {
      return (
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-11 gap-1">
            {Array.from({ length: 11 }).map((_, npsVal) => (
              <button
                key={npsVal}
                type="button"
                onClick={() => handleAnswerChange(q.id, npsVal, true)}
                className={`h-9 sm:h-10 rounded-lg border text-[12px] sm:text-[13px] font-medium transition-all cursor-pointer ${
                  answers[q.id] === npsVal
                    ? "bg-blue-600 text-white border-blue-600 shadow-md scale-105"
                    : "bg-white border-gray-200 text-black hover:border-blue-400 hover:bg-blue-50/50"
                }`}
                style={answers[q.id] === npsVal ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
              >
                {npsVal}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[11px] font-normal text-gray-500">
            <span>{q.lowestValueLabel || "Not likely at all"}</span>
            <span>{q.highestValueLabel || "Very likely"}</span>
          </div>
        </div>
      );
    }

    // Smiley / Emoji Rating (1-5)
    if (q.type === "smiley" || q.npsStyle === "emoji" || q.ratingType === "emoji" || q.subtype === "emoji") {
      const emojis = ["😠", "😕", "😐", "🙂", "😃"];
      return (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between gap-2 py-3 px-1">
            {emojis.map((emoji, idx) => {
              const val = idx + 1;
              const isSelected = answers[q.id] === val || answers[q.id] === emoji;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleAnswerChange(q.id, val, true)}
                  className={`flex-1 py-3 px-2 rounded-2xl border transition-all duration-200 flex flex-col items-center justify-center cursor-pointer ${
                    isSelected
                      ? "bg-amber-50 border-amber-400 shadow-md scale-110 -translate-y-1"
                      : "bg-white border-gray-200 hover:border-amber-300 hover:bg-amber-50/30"
                  }`}
                >
                  <span className="text-2xl sm:text-3xl transition-transform hover:scale-125">
                    {emoji}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex justify-between text-[11px] font-normal text-gray-500 px-1">
            <span>{q.lowestValueLabel || "Very Unsatisfied"}</span>
            <span>{q.highestValueLabel || "Very Satisfied"}</span>
          </div>
        </div>
      );
    }

    // Rating / CSAT / Stars 1-5
    if (q.type === "csat" || q.type === "ces" || q.type === "rating") {
      return (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-center gap-2.5 py-3">
            {[1, 2, 3, 4, 5].map((starVal) => (
              <button
                key={starVal}
                type="button"
                onClick={() => handleAnswerChange(q.id, starVal, true)}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  answers[q.id] === starVal
                    ? "bg-amber-50 border-amber-400 text-amber-500 scale-110 shadow-sm"
                    : "bg-white border-gray-200 text-gray-300 hover:border-amber-300 hover:text-amber-400"
                }`}
              >
                <Star className={`w-6 h-6 ${answers[q.id] >= starVal ? "fill-amber-400 text-amber-400" : ""}`} />
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[11px] font-normal text-gray-500 px-1">
            <span>{q.lowestValueLabel || "Very Unsatisfied"}</span>
            <span>{q.highestValueLabel || "Very Satisfied"}</span>
          </div>
        </div>
      );
    }

    // Single Choice
    if (q.type === "single_choice") {
      return (
        <div className="space-y-2 pt-2">
          {(q.options || ["Option 1", "Option 2", "Option 3"]).map((opt, oIdx) => (
            <button
              key={oIdx}
              type="button"
              onClick={() => handleAnswerChange(q.id, opt, true)}
              className={`w-full p-3 text-left border rounded-xl text-[13px] font-medium transition-all flex items-center justify-between cursor-pointer ${
                answers[q.id] === opt
                  ? "border-blue-600 bg-blue-50/50 text-blue-700 shadow-2xs"
                  : "border-gray-200 text-black hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <span>{opt}</span>
              {answers[q.id] === opt && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
            </button>
          ))}
        </div>
      );
    }

    // Multiple Choice
    if (q.type === "multiple_choice" || q.type === "multiple-choice" || q.type === "checkbox") {
      return (
        <div className="space-y-2 pt-2">
          {(q.options || ["Option A", "Option B", "Option C"]).map((opt, oIdx) => {
            const selectedList = Array.isArray(answers[q.id]) ? answers[q.id] : [];
            const isChecked = selectedList.includes(opt);

            return (
              <button
                key={oIdx}
                type="button"
                onClick={() => toggleMultiChoice(q.id, opt)}
                className={`w-full p-3 text-left border rounded-xl text-[13px] font-medium transition-all flex items-center justify-between cursor-pointer ${
                  isChecked
                    ? "border-blue-600 bg-blue-50/50 text-blue-700 shadow-2xs"
                    : "border-gray-200 text-black hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <span>{opt}</span>
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  isChecked ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 bg-white"
                }`}>
                  {isChecked && <Check className="w-3 h-3" />}
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    // Short / Long Text
    return (
      <textarea
        rows={3}
        placeholder="Type your answer here..."
        value={answers[q.id] || ""}
        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
        className="w-full p-3 border border-gray-200 rounded-xl text-[13px] text-black focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-normal resize-none"
      />
    );
  };

  // 1. LARGE FORMAT (Centered Screen Overlay Modal)
  if (isLargeFormat) {
    return (
      <div
        className="nodrag fixed inset-0 z-[10000005] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 10000005,
        }}
      >
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 sm:p-8 flex flex-col max-h-[85vh] overflow-y-auto animate-in zoom-in-95">
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onDismiss) onDismiss(survey._id, shareConfig.reshowAfterDismiss);
              else if (onClose) onClose();
            }}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Top Progress Bar */}
          {showProgressBar && questions.length > 0 && !submitted && !isSinglePageForm && (
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-5 mt-2">
              <div
                className="h-1.5 transition-all duration-300 rounded-full"
                style={{
                  width: `${((currentStep + 1) / questions.length) * 100}%`,
                  backgroundColor: primaryColor,
                }}
              />
            </div>
          )}

          {/* Header */}
          <div className="border-b border-gray-100 pb-4 mb-5 flex items-center justify-between">
            <div>
              <h1 className="text-[20px] font-semibold text-black tracking-tight">
                {survey?.name || "Feedback Survey"}
              </h1>
              {questions.length > 0 && !submitted && !isSinglePageForm && (
                <p className="text-[12px] font-medium mt-0.5" style={{ color: primaryColor }}>
                  Question {currentStep + 1} of {questions.length}
                </p>
              )}
            </div>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmitSurvey} className="space-y-6 flex-1">
              {isSinglePageForm ? (
                <div className="space-y-6 pr-1">
                  {questions.map((q, idx) => (
                    <div key={q.id || idx} className="space-y-3 pb-4 border-b border-gray-100 last:border-0">
                      <div>
                        <h3 className="text-[15px] font-medium text-black">
                          {idx + 1}. {q.question}
                        </h3>
                        {q.subtitle && <p className="text-[12px] text-gray-500 font-normal mt-0.5">{q.subtitle}</p>}
                      </div>
                      {renderQuestionInput(q)}
                    </div>
                  ))}

                  {!knownEmail && optionsConfig.collectEmail !== false && (
                    <div className="pt-2 space-y-3">
                      <span className="text-[13px] font-medium text-black block">Your Info (Optional)</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Your Name"
                          value={respondentName}
                          onChange={(e) => setRespondentName(e.target.value)}
                          className="p-3 border border-gray-200 rounded-xl text-[13px] text-black focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="email"
                          placeholder="Your Email Address"
                          value={respondentEmail}
                          onChange={(e) => setRespondentEmail(e.target.value)}
                          className="p-3 border border-gray-200 rounded-xl text-[13px] text-black focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white font-medium py-3 rounded-xl text-[14px] transition-colors shadow-md cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Survey"}
                  </button>
                </div>
              ) : (
                /* Step by Step */
                <div className="space-y-6">
                  {questions[currentStep] && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-[17px] font-medium text-black">
                          {questions[currentStep].question}
                        </h3>
                        {questions[currentStep].subtitle && (
                          <p className="text-[13px] text-gray-500 font-normal mt-1">
                            {questions[currentStep].subtitle}
                          </p>
                        )}
                      </div>
                      {renderQuestionInput(questions[currentStep])}
                    </div>
                  )}

                  {currentStep === questions.length - 1 && !knownEmail && optionsConfig.collectEmail !== false && (
                    <div className="pt-4 border-t border-gray-100 space-y-3">
                      <span className="text-[13px] font-medium text-black block">Your Info (Optional)</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Your Name"
                          value={respondentName}
                          onChange={(e) => setRespondentName(e.target.value)}
                          className="p-3 border border-gray-200 rounded-xl text-[13px] text-black focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="email"
                          placeholder="Your Email Address"
                          value={respondentEmail}
                          onChange={(e) => setRespondentEmail(e.target.value)}
                          className="p-3 border border-gray-200 rounded-xl text-[13px] text-black focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    {currentStep > 0 ? (
                      <button
                        type="button"
                        onClick={() => setCurrentStep((prev) => prev - 1)}
                        className="text-[14px] font-medium text-gray-600 hover:text-black transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <div />
                    )}

                    {currentStep < questions.length - 1 ? (
                      <button
                        type="button"
                        onClick={() => setCurrentStep((prev) => prev + 1)}
                        className="text-white font-medium px-6 py-2.5 rounded-xl text-[14px] transition-colors shadow-sm flex items-center gap-2 cursor-pointer"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="text-white font-medium px-6 py-2.5 rounded-xl text-[14px] transition-colors shadow-md cursor-pointer disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Survey"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </form>
          ) : (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-[20px] font-semibold text-black">
                {thankYouCard?.title || "Thanks for your feedback!"}
              </h2>
              <p className="text-[14px] font-normal text-gray-600 max-w-sm mx-auto">
                {thankYouCard?.subtitle || thankYouCard?.message || "Your response has been successfully recorded."}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-100 pt-4 mt-6 flex items-center justify-between gap-2 text-[12px] font-medium">
            <div className="flex items-center gap-3 flex-wrap">
              {(shareConfig.footerLinks || []).map((link, idx) =>
                link.label && link.url ? (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: sharePage.linkColor || "#64748B" }}
                    className="hover:underline transition-colors"
                  >
                    {link.label}
                  </a>
                ) : null
              )}
            </div>
            <div className="text-gray-400 font-normal text-[11px]">
              Powered by <span className="font-semibold text-gray-600">DexKor</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. SMALL CORNER WIDGET FORMAT (Default Floating Modal)
  return (
    <div
      className={`nodrag dexkor-survey-widget-modal absolute z-[1000003] select-none ${
        isLeftAligned ? "align-left" : ""
      }`}
      style={{
        bottom: "74px",
        [isLeftAligned ? "left" : "right"]: "0px",
      }}
    >
      <div className="relative w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.22)] border border-slate-100 overflow-hidden text-slate-800 font-sans p-4 sm:p-5">
        {/* Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onDismiss) onDismiss(survey._id, shareConfig.reshowAfterDismiss);
            else if (onClose) onClose();
          }}
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer z-20"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress Bar */}
        {showProgressBar && questions.length > 0 && !submitted && !isSinglePageForm && (
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden mb-4 pr-6">
            <div
              className="h-1.5 transition-all duration-300 rounded-full"
              style={{
                width: `${((currentStep + 1) / questions.length) * 100}%`,
                backgroundColor: primaryColor,
              }}
            />
          </div>
        )}

        {/* Header */}
        <div className="border-b border-slate-100 pb-3 mb-4 pr-6">
          <h4 className="text-[15px] font-semibold text-slate-900 truncate m-0">
            {survey?.name || "Feedback Survey"}
          </h4>
          {questions.length > 0 && !submitted && !isSinglePageForm && (
            <p className="text-[11px] font-medium mt-0.5" style={{ color: primaryColor }}>
              Question {currentStep + 1} of {questions.length}
            </p>
          )}
        </div>

        {submitted ? (
          <div className="py-6 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h3 className="text-base font-bold text-slate-900 m-0">
              {thankYouCard?.title || "Thanks for your feedback!"}
            </h3>
            <p className="text-xs text-slate-500 max-w-[240px] mx-auto m-0">
              {thankYouCard?.subtitle || thankYouCard?.message || "Your response has been recorded."}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmitSurvey} className="space-y-4">
            {isSinglePageForm ? (
              <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                {questions.map((q, idx) => (
                  <div key={q.id || idx} className="space-y-2 pb-3 border-b border-slate-100 last:border-0">
                    <div>
                      <h4 className="text-[13px] font-semibold text-slate-900 m-0">
                        {idx + 1}. {q.question}
                      </h4>
                      {q.subtitle && <p className="text-[11px] text-slate-500 m-0 mt-0.5">{q.subtitle}</p>}
                    </div>
                    {renderQuestionInput(q)}
                  </div>
                ))}

                {!knownEmail && optionsConfig.collectEmail !== false && (
                  <div className="pt-2 space-y-2">
                    <span className="text-[12px] font-medium text-slate-700 block">Your Info (Optional)</span>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={respondentName}
                      onChange={(e) => setRespondentName(e.target.value)}
                      className="w-full p-2.5 border border-slate-200 rounded-xl text-[12px] outline-none"
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={respondentEmail}
                      onChange={(e) => setRespondentEmail(e.target.value)}
                      className="w-full p-2.5 border border-slate-200 rounded-xl text-[12px] outline-none"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 text-white font-semibold rounded-xl text-[13px] transition-all shadow-md cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Survey"}
                </button>
              </div>
            ) : (
              /* Step-by-Step */
              <div className="space-y-4">
                {questions[currentStep] && (
                  <div className="space-y-2">
                    <h4 className="text-[14px] font-bold text-slate-900 m-0 leading-snug">
                      {questions[currentStep].question}
                    </h4>
                    {questions[currentStep].subtitle && (
                      <p className="text-[12px] text-slate-500 m-0">{questions[currentStep].subtitle}</p>
                    )}
                    {renderQuestionInput(questions[currentStep])}
                  </div>
                )}

                {currentStep === questions.length - 1 && !knownEmail && optionsConfig.collectEmail !== false && (
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <span className="text-[11px] font-medium text-slate-500 block">Your Info (Optional)</span>
                    <input
                      type="email"
                      placeholder="Your Email Address"
                      value={respondentEmail}
                      onChange={(e) => setRespondentEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[12px] outline-none"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  {currentStep > 0 ? (
                    <button
                      type="button"
                      onClick={() => setCurrentStep((prev) => prev - 1)}
                      className="text-[12px] font-medium text-slate-500 hover:text-slate-800 transition-colors"
                    >
                      Previous
                    </button>
                  ) : (
                    <div />
                  )}

                  {currentStep < questions.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => setCurrentStep((prev) => prev + 1)}
                      className="px-4 py-2 rounded-xl text-white text-[13px] font-semibold flex items-center gap-1 shadow-md cursor-pointer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <span>Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-xl text-white text-[13px] font-semibold shadow-md cursor-pointer disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Survey"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>
        )}

        {/* Footer */}
        <div className="border-t border-slate-100 pt-3 mt-4 flex items-center justify-between gap-1 text-[11px] font-medium text-slate-400">
          <div className="flex items-center gap-2 flex-wrap">
            {(shareConfig.footerLinks || []).map((link, idx) =>
              link.label && link.url ? (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: sharePage.linkColor || "#64748B" }}
                  className="hover:underline"
                >
                  {link.label}
                </a>
              ) : null
            )}
          </div>
          <div>
            Powered by <span className="font-semibold text-slate-600">DexKor</span>
          </div>
        </div>
      </div>
    </div>
  );
}
